import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import { TextInput } from '../TextInput/TextInput.esm.js';

// Static error messages mapped to validation states
const ERROR_MESSAGES = {
    valueMissing: "Phone number is required",
    patternMismatch: "Please enter a valid phone number",
    customError: "Invalid phone number",
};
// US Phone number regex for (###) ###-#### format
const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
// Regex to extract just digits from input
const DIGITS_ONLY_REGEX = /\D/g;
/**
 * Formats a string of digits into US phone number format: (###) ###-####
 */
const formatPhoneNumber = (digits) => {
    // Remove all non-digits
    const cleanDigits = digits.replace(DIGITS_ONLY_REGEX, "");
    // Limit to 10 digits
    const limitedDigits = cleanDigits.slice(0, 10);
    // Apply formatting based on length
    if (limitedDigits.length >= 7) {
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
    else if (limitedDigits.length >= 4) {
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    }
    else if (limitedDigits.length > 0) {
        return `(${limitedDigits}`;
    }
    return "";
};
const PhoneNumberInput = forwardRef((props, ref) => {
    const { validateOn = "blur", customValidation, autoFormat = true, required = false, disabled = false, value, onChange, onBlur } = props, textInputProps = __rest(props, ["validateOn", "customValidation", "autoFormat", "required", "disabled", "value", "onChange", "onBlur"]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [blurAttempted, setBlurAttempted] = useState(false);
    const [inErrorState, setInErrorState] = useState(false);
    const [validationState, setValidationState] = useState({
        valid: true,
        valueMissing: false,
        patternMismatch: false,
        customError: false,
    });
    const inputRef = useRef(null);
    const validatePhoneNumber = useCallback(() => {
        const phoneValue = String(value || "");
        const isValueMissing = required && !phoneValue.trim();
        const isPatternMismatch = Boolean(phoneValue.trim() && !PHONE_REGEX.test(phoneValue));
        let isCustomError = false;
        if (customValidation && phoneValue) {
            const customError = customValidation(phoneValue);
            isCustomError = !!customError;
        }
        const isValid = !isValueMissing && !isPatternMismatch && !isCustomError;
        return {
            valid: isValid,
            valueMissing: isValueMissing,
            patternMismatch: isPatternMismatch,
            customError: isCustomError,
        };
    }, [value, required, customValidation]);
    // Update validation state reactively
    // - In submit-only mode: skip reactive validation unless in error state
    // - In blur mode: skip reactive validation unless in error state
    // - When in error state: validate on every change until error is resolved
    useEffect(() => {
        // If in error state, always validate on change
        if (inErrorState) {
            const newValidationState = validatePhoneNumber();
            setValidationState(newValidationState);
            // If validation passes, exit error state and revert to validateOn mode
            if (newValidationState.valid) {
                setInErrorState(false);
            }
            return;
        }
        // Not in error state: follow normal validateOn logic
        if (validateOn === "submit")
            return; // skip reactive validation
        const newValidationState = validatePhoneNumber();
        setValidationState(newValidationState);
    }, [
        value,
        required,
        customValidation,
        blurAttempted,
        validateOn,
        validatePhoneNumber,
        inErrorState,
    ]);
    const handleChange = (e) => {
        let newValue = e.target.value;
        // Apply auto-formatting if enabled
        if (autoFormat) {
            newValue = formatPhoneNumber(newValue);
        }
        // Create a new event with the formatted value
        const formattedEvent = Object.assign(Object.assign({}, e), { target: Object.assign(Object.assign({}, e.target), { value: newValue }) });
        onChange === null || onChange === void 0 ? void 0 : onChange(formattedEvent);
    };
    const handleBlur = (e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        if (validateOn === "submit")
            return; // no blur validation in submit-only mode
        setBlurAttempted(true);
        // Trigger validation after blur
        const newValidationState = validatePhoneNumber();
        setValidationState(newValidationState);
        // If validation fails, enter error state for onChange validation
        if (!newValidationState.valid) {
            setInErrorState(true);
        }
    };
    const getErrorMessage = (validity) => {
        if (validity.valid)
            return undefined;
        if (validity.valueMissing)
            return ERROR_MESSAGES.valueMissing;
        if (validity.patternMismatch)
            return ERROR_MESSAGES.patternMismatch;
        if (validity.customError)
            return ERROR_MESSAGES.customError;
        return undefined;
    };
    const shouldShowValidation = () => {
        if (validateOn === "submit")
            return submitAttempted;
        if (validateOn === "blur")
            return blurAttempted;
        return false;
    };
    const hasError = !validationState.valid && shouldShowValidation();
    const errorMessage = hasError ? getErrorMessage(validationState) : undefined;
    useEffect(() => {
        var _a;
        if (validateOn !== "submit")
            return;
        const form = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.closest("form");
        if (!form)
            return;
        const handleFormSubmit = (e) => {
            const state = validatePhoneNumber();
            setSubmitAttempted(true);
            setValidationState(state);
            // If validation fails, enter error state for onChange validation
            if (!state.valid) {
                setInErrorState(true);
                e.preventDefault();
                return;
            }
        };
        form.addEventListener("submit", handleFormSubmit);
        return () => form.removeEventListener("submit", handleFormSubmit);
    }, [validateOn, validatePhoneNumber]);
    return (jsx(TextInput, Object.assign({}, textInputProps, { type: "tel", disabled: disabled, value: value, onChange: handleChange, onBlur: handleBlur, error: hasError, errorMessage: errorMessage, ref: inputRef })));
});
PhoneNumberInput.displayName = "PhoneNumberInput";

export { ERROR_MESSAGES, PhoneNumberInput };
